import { useEffect, useCallback, useRef } from 'react';
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout';
import { useKeyboardShortcuts, useAutoSave, useProducts } from '@/hooks';
import { useDesignStore, useProductSelectionStore, useUIStore } from '@/stores';
import { isGuidedEntryPath } from '@/lib/creator-entry';
import { postEmbedMessage } from '@/lib/embed-messages';
import { useGuidedEntryStore } from '@/stores/guidedEntryStore';
import { isKnownStep, normalizeStepForMode } from '@/stores/creatorSteps';
import { usePublishStore } from '@/stores/publishStore';
import {
  resolvePublishHydration,
  untitledDesignPlaceholders,
} from '@/lib/publishHydration';
import { buildPrintContextParamsForPosition } from '@/lib/printContextBuilder';
import { hasCreatorDesign } from '@/utils/hasCreatorDesign';
import {
  guidedPreselectionFrom,
  resolveInitialSelection,
  skuFromSearchParams,
} from './creatorInitialSelection';
import { parseProductsFilterParam } from '@/lib/creatorProductsFilter';

/**
 * Creator Page - main design editor page
 *
 * URL Structure:
 * - /creator                           → New design (classic editor)
 * - /creator/guided                    → New design (motif first)
 * - /creator/:designId                 → Load existing design
 * - /creator?product=xxx&color=yyy     → New design with product/color pre-selected
 * - /creator?SKU=xxx                   → New design with product matched by variant SKU
 * - /creator?step=products             → Open products step directly
 * - /creator/guided?procedure=embroidery → Guided entry in the iframe starting
 *                                         on Stick (or `print`); without it the
 *                                         start follows the preselected product
 *                                         (see resolveStartProcedure)
 * - /creator/:designId?product=xxx     → Load design with product pre-selected
 * - ?primaryColor=%232b4cff            → Override the brand color for this
 *                                         embed (read once at app startup —
 *                                         see main.tsx / src/lib/theme.ts —
 *                                         not re-applied on route changes)
 */
export function CreatorPage() {
  const { t } = useTranslation();
  const { designId } = useParams<{ designId?: string }>();
  const { pathname } = useLocation();
  const isGuidedRoute = isGuidedEntryPath(pathname);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL Query Parameters (all optional)
  const productIdFromUrl = searchParams.get('product');
  const colorIdFromUrl = searchParams.get('color');
  const stepFromUrl = searchParams.get('step');
  const filterFromUrl = searchParams.get('filter');
  const skuFromUrl = skuFromSearchParams(searchParams);
  const editProductFromUrl = searchParams.get('editProduct');
  const isExplicitNew = searchParams.get('new') === '1';

  // Design store
  const {
    loadDesign,
    saveDesign,
    clearDesign,
    syncError,
    designId: currentDesignId,
  } = useDesignStore();

  // Product selection store
  const setFocusedProduct = useProductSelectionStore(
    (state) => state.setFocusedProduct
  );
  const toggleProduct = useProductSelectionStore(
    (state) => state.toggleProduct
  );
  const toggleColor = useProductSelectionStore((state) => state.toggleColor);
  const deactivateAll = useProductSelectionStore(
    (state) => state.deactivateAll
  );
  const setActivePosition = useProductSelectionStore(
    (state) => state.setActivePosition
  );
  const resetProductSelection = useProductSelectionStore(
    (state) => state.reset
  );

  // Step store
  const currentStep = useUIStore((state) => state.currentStep);
  const setCurrentStep = useUIStore((state) => state.setCurrentStep);
  const setProductsStatusFilter = useUIStore(
    (state) => state.setProductsStatusFilter
  );
  // Subscribed, not read once: loadDesign decides the mode of an existing
  // design asynchronously, and the URL step has to follow that decision.
  const creatorEntryMode = useUIStore((state) => state.creatorEntryMode);

  // Single-product mode
  const setSingleProductMode = useUIStore(
    (state) => state.setSingleProductMode
  );
  const singleProductMode = useUIStore((state) => state.singleProductMode);

  // Fetch products for default selection
  const { data: products } = useProducts();

  // Single source of truth for "the initial product selection is decided".
  // Covers all three entry shapes (?product=, ?SKU=, default) plus the
  // unresolvable case, so a failed lookup never retries or falls through.
  const hasResolvedInitialSelection = useRef(false);
  // Track the last loaded design ID to prevent duplicate loads
  const lastLoadedDesignId = useRef<string | null>(null);
  // Track if we're currently loading a design to prevent concurrent loads
  const isLoadingDesign = useRef(false);
  // Track if we've already cleared for this fresh visit (prevent double-clear)
  const hasCleared = useRef(false);
  // Track if a forced re-load was requested (e.g. exiting single-product mode)
  const forceReloadRef = useRef(false);
  // Track if this is a fresh visit (no designId in URL)
  const isFreshCreatorVisit = !designId;

  // Polaris embed only (lib/embed-messages.ts): tell the host the Creator is up.
  useEffect(() => {
    postEmbedMessage({ type: 'designdeck:embed:ready' });
  }, []);

  // Clear singleProductMode on unmount so stale mode doesn't bleed into
  // the next creator session (e.g. home → "Create New Design").
  useEffect(() => {
    return () => {
      useUIStore.getState().setSingleProductMode(null);
    };
  }, []);

  useEffect(() => {
    // The route decides the entry: /creator/guided is guided, /creator is
    // classic. Set once on mount, then the mode lives in uiStore — step
    // navigation rewrites the path, so re-reading it later would flip the
    // seller back to classic mid-flow.
    //
    // New designs only: the guided store is in-memory, so an existing design
    // would open with empty motif cards while its positions sit in the
    // database, and the surface would read that emptiness as a removal.
    useUIStore
      .getState()
      .setCreatorEntryMode(isGuidedRoute && !designId ? 'guided' : 'classic');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize keyboard shortcuts
  useKeyboardShortcuts();

  // Auto-save (only when we have a designId)
  useAutoSave({
    enabled: !!currentDesignId,
    delay: 3000,
  });

  // Save handler for Ctrl+S — uses saveDesign() as single source of truth
  const handleSave = useCallback(async () => {
    const result = await saveDesign();
    if (result) {
      toast.success(t('creator.page.saved'));
      if (!currentDesignId && result.id) {
        navigate(`/creator/${result.id}`, { replace: true });
      }
    }
  }, [currentDesignId, saveDesign, navigate, t]);

  // Register Ctrl+S shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  // Apply step from URL (if provided).
  // `?filter=` preselects the products-step status filter; the overview's
  // "open" link uses it to show the listing's own products (creatorProductsFilter).
  useEffect(() => {
    const filter = parseProductsFilterParam(filterFromUrl);
    if (filter) setProductsStatusFilter(filter);
  }, [filterFromUrl, setProductsStatusFilter]);

  // For new designs (no designId), block Products/Publish until a design exists.
  // Existing designs may still be loading — don't gate them here (Header gates UI).
  useEffect(() => {
    if (!stepFromUrl) return;
    const lowered = stepFromUrl.toLowerCase();
    if (!isKnownStep(lowered)) return;
    // ?step=catalog on a classic design lands on products, not on a step
    // that mode does not have. Re-runs when a loaded design flips the mode
    // to guided, so a reload at the catalogue comes back to the catalogue.
    const normalizedStep = normalizeStepForMode(lowered, creatorEntryMode);

    if (!designId && normalizedStep !== 'design' && !hasCreatorDesign()) {
      toast.error(t('creator.toast.designRequired'));
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('step', 'design');
      navigate(`/creator?${nextParams.toString()}`, { replace: true });
      if (currentStep !== 'design') {
        setCurrentStep('design');
      }
      return;
    }

    if (currentStep !== normalizedStep) {
      setCurrentStep(normalizedStep);
    }
  }, [
    stepFromUrl,
    currentStep,
    setCurrentStep,
    searchParams,
    navigate,
    designId,
    creatorEntryMode,
    t,
  ]);

  // Track previous editProduct param to detect mode transitions
  const prevEditProductRef = useRef<string | null>(null);

  // Activate single-product mode from URL and force re-load when mode changes
  useEffect(() => {
    const prevEditProduct = prevEditProductRef.current;
    prevEditProductRef.current = editProductFromUrl;

    if (editProductFromUrl) {
      setSingleProductMode({ productId: editProductFromUrl });

      // Force re-load when entering/switching single-product mode so
      // loadDesign picks the correct master product for canvas data.
      if (designId && prevEditProduct !== editProductFromUrl) {
        lastLoadedDesignId.current = null;
      }
    } else if (prevEditProduct !== null || singleProductMode) {
      // Keyed on the URL transition, not only on the store flag: the header's
      // "Produkte speichern" clears the flag before it navigates, and the
      // reload below is what puts the master product's positions back —
      // without it every card kept showing the edited product's placement.
      setSingleProductMode(null);

      // Force re-load when exiting single-product mode so
      // the original master product positions are restored.
      if (designId && prevEditProduct !== null) {
        lastLoadedDesignId.current = null;
        forceReloadRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProductFromUrl, setSingleProductMode, designId]);

  // Load design on mount or create new.
  // Returning from the Products step does NOT reload the design: the stores
  // already hold the current state (product toggles, per-product placements)
  // and CanvasEditor restores the master from masterPositions on remount.
  // Only leaving single-product mode (editProduct param removed) forces a
  // reload, see the effect above.
  useEffect(() => {
    const initializeDesign = async () => {
      if (designId) {
        // Reset the clear guard so navigating back to /creator will clear again
        hasCleared.current = false;
        // Skip loading if this exact design was already loaded in this
        // session. lastLoadedDesignId is the authoritative guard — effects
        // intentionally set it to null to force a re-load (e.g. after
        // exiting single-product mode), so storeDesignId/currentDesignId
        // alone are not sufficient to skip.
        if (
          lastLoadedDesignId.current === designId &&
          isLoadingDesign.current === false
        ) {
          return;
        }

        // Also skip if the design store already has this designId
        // (e.g., just created via saveDesign/createDesign during step navigation).
        // This prevents clearAllPositions() from wiping the canvas state
        // that was just saved.
        // EXCEPTION: Do NOT skip when entering/switching single-product mode
        // — we must re-load so the correct master product positions are loaded.
        // EXCEPTION 2: Do NOT skip when exiting single-product mode
        // — we must re-load so the original master product positions are restored.
        const storeDesignId = useDesignStore.getState().designId;
        if (
          storeDesignId === designId &&
          lastLoadedDesignId.current === null &&
          isLoadingDesign.current === false &&
          !editProductFromUrl &&
          !forceReloadRef.current
        ) {
          lastLoadedDesignId.current = designId;
          return;
        }
        // Clear force-reload flag after it's been consumed
        forceReloadRef.current = false;

        // Prevent concurrent loads
        if (isLoadingDesign.current) return;

        // Mark as loading and set ref immediately to prevent concurrent loads
        isLoadingDesign.current = true;
        lastLoadedDesignId.current = designId;

        // Another design is about to replace the loaded one: its publish
        // fields, the AI suggestion status and any in-flight suggestion must
        // not carry over. Reloading the same design (single-product mode)
        // keeps them.
        const isNewDesignLoad = storeDesignId !== designId;
        if (isNewDesignLoad) {
          usePublishStore.getState().reset();
        }

        try {
          // Load existing design
          const design = await loadDesign(designId);
          if (!design) {
            toast.error(t('errors.design_not_found'));
            navigate('/creator');
            return;
          }

          // A design published once carries the shop's title, description
          // and tags. The reset above cleared the Publish step for the
          // design that is about to replace the loaded one; this puts the
          // new design's own saved values back. A draft's placeholder name
          // stays out of the title, and a saved title keeps the AI
          // auto-start on the products step from replacing it (see
          // lib/publishHydration). Only on a genuine design switch:
          // reloading the SAME design (single-product mode) must not clobber
          // fields the seller may already be editing on this visit.
          if (isNewDesignLoad) {
            usePublishStore
              .getState()
              .hydrateFromDesign(
                resolvePublishHydration(design, untitledDesignPlaceholders())
              );
          }

          // If opening placement edit with a specific color, override the
          // focusedColorId that loadFromDesign reset to the first saved color.
          if (editProductFromUrl && colorIdFromUrl) {
            setFocusedProduct(editProductFromUrl, colorIdFromUrl);
          }
          // Verify designId is set in store after loadDesign completes
          const updatedStoreDesignId = useDesignStore.getState().designId;
          if (designId && updatedStoreDesignId !== designId) {
            console.warn(
              'Design loaded but designId not set in store, setting manually'
            );
            // Manually set it if needed
            useDesignStore.getState().setDesignId(designId);
          }
        } finally {
          isLoadingDesign.current = false;
        }
      } else if (!hasCleared.current) {
        // Visiting /creator without a designId in the URL.
        // If the store still holds a saved designId (e.g. user navigated
        // back after saving) and this is NOT an explicit "new design"
        // request, redirect to /creator/{id} to restore the design.
        // The guided route always starts a new design: its mode was set on
        // mount from the URL, and redirecting into a stored design would open
        // that design under the guided surface (same page instance, so the
        // mount effect does not run again).
        const storeDesignId = useDesignStore.getState().designId;
        if (storeDesignId && !isExplicitNew && !isGuidedRoute) {
          navigate(`/creator/${storeDesignId}`, { replace: true });
          return;
        }

        // Truly fresh visit — clear state for a new design.
        hasCleared.current = true;
        clearDesign();
        resetProductSelection();
        usePublishStore.getState().reset();
        // The guided store is in-memory for the whole SPA session; motifs from
        // an earlier guided design must not surface in a new one.
        useGuidedEntryStore.getState().reset();
        setCurrentStep('design');
        hasResolvedInitialSelection.current = false;
        lastLoadedDesignId.current = null;
        isLoadingDesign.current = false;
      }
    };

    initializeDesign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    designId,
    currentStep,
    loadDesign,
    clearDesign,
    resetProductSelection,
    setCurrentStep,
    navigate,
    editProductFromUrl,
    isExplicitNew,
    t,
  ]);

  // Resolve the Creator's initial product selection.
  //
  // One effect, one decision. The precedence (?product= -> ?SKU= -> default)
  // and the per-source colour rules live in resolveInitialSelection so they can
  // be tested without mounting this page.
  useEffect(() => {
    if (hasResolvedInitialSelection.current) {
      return;
    }

    const selection = resolveInitialSelection({
      productIdFromUrl,
      colorIdFromUrl,
      skuFromUrl,
      products,
      isFreshCreatorVisit,
    });

    if (selection.status === 'wait' || selection.status === 'skip') {
      return;
    }

    // Mark decided before applying: an unresolvable product or SKU is a final
    // answer, not something to retry on every dependency change.
    hasResolvedInitialSelection.current = true;

    // Step 1 of the guided entry reads this for its hint and its start
    // procedure. Recorded before the error return: an unresolvable name is an
    // answer too, and step 1 would otherwise wait for one forever.
    if (isGuidedRoute) {
      const preselection = guidedPreselectionFrom(selection);
      if (preselection) {
        useGuidedEntryStore.getState().setPreselection(preselection);
      }
    }

    if (selection.status === 'error') {
      toast.error(selection.message);
      return;
    }

    const { product, colorId, exclusive } = selection;

    if (exclusive) {
      deactivateAll();
    }

    // The guided entry picks products in its catalogue step, so the default
    // product is only the reference for print areas and sizes — focused, not
    // selected. A product named in the URL is a deliberate choice and stays
    // selected in both modes.
    const preselect = !(isGuidedRoute && selection.source === 'default');

    if (preselect) {
      toggleProduct(product.id, true);

      if (colorId) {
        toggleColor(product.id, colorId);
      }
    }

    const frontPosition = product.positions?.find(
      (pos) => pos.slug === 'front'
    );
    const frontParams = frontPosition
      ? buildPrintContextParamsForPosition(product, 'front')
      : null;
    if (frontPosition && frontParams) {
      setActivePosition('front', frontPosition.id, frontParams);
    }

    setFocusedProduct(product.id, colorId);
  }, [
    productIdFromUrl,
    colorIdFromUrl,
    skuFromUrl,
    isGuidedRoute,
    products,
    isFreshCreatorVisit,
    deactivateAll,
    toggleProduct,
    toggleColor,
    setActivePosition,
    setFocusedProduct,
  ]);

  // Show error toast on sync error
  useEffect(() => {
    if (syncError) {
      toast.error(syncError);
    }
  }, [syncError]);

  // No full-page loading state: MainLayout doesn't render children, the
  // canvas has its own loading overlay, and a full-screen spinner on every
  // loadDesign would itself be a flicker.
  return <MainLayout />;
}
